// The microphone side of voice notes — the web's VoiceRecorder.swift.
//
// ONE recorder for the whole page (there is one microphone): the composer that started it owns the
// strip, any other composer's mic waits. Same contract as the apps: paused holds the take (only
// cancel throws it away), the clock counts RECORDED time (VoiceRecordingClock), takes under 0.7 s are
// mis-taps and are dropped on send.
//
// Interruptions (VoiceRecorder.observeInterruptions + ChannelView's scenePhase + Android's
// VoiceRecordingInterruptions), on a browser's terms:
//  - the microphone track ends or is muted by the system (unplugged, another app or a phone call
//    takes it, iOS Safari's own interruption) → PAUSE, never resume by itself;
//  - a call starting in this tab → PAUSE (the calls feature dispatches `satori:media-interrupt`, or
//    calls `pauseVoiceRecording()`);
//  - the tab hidden → PAUSE on a phone/tablet only (the app going to the background). On a desktop
//    the Mac rule applies: switching windows while dictating is normal, a take that paused itself
//    every time would be unusable (MacVoiceRecordingStrip);
//  - the page unloading → discard (nothing can be sent from a dying page).
// While recording on a phone the screen is kept awake (Wake Lock) — "so it doesn't sleep and it
// doesn't stop recording" (user, 2026-09-12).
import { ScreenAwake } from '../../../core/screenAwake';
import { screenAwake } from '../../screenAwake';
import { useSyncExternalStore } from 'react';
import { VoiceRecordingClock } from '../../../core/voiceNoteControls';
import { remuxFragmented, probeMp4, writeAudioMp4, mp4aSampleEntry, audioSpecificConfig, type Mp4Sample } from './mp4Audio';
import {
  chooseVoiceEncoder,
  floatTo16,
  meterLevel,
  micErrorKind,
  voiceNoteName,
  VOICE_BITRATE,
  VOICE_MIN_DURATION,
  VOICE_SAMPLE_RATE,
  type MicError,
  type VoiceEncoderChoice,
  type VoiceEncoderKind,
} from './voiceFormat';
import workletUrl from './capture-worklet.js?url';

export type RecorderPhase = 'idle' | 'starting' | 'recording' | 'paused';

export interface RecorderState {
  phase: RecorderPhase;
  /** The composer key that owns the take (and draws the strip). */
  owner: string | null;
  /** Seconds of audio in the take. */
  elapsed: number;
  /** The last refusal, for the composer that asked (cleared by the next start or dismiss). */
  error: { owner: string; kind: MicError } | null;
  format: VoiceEncoderKind | null;
}

export interface VoiceTake {
  file: File;
  duration: number;
  format: VoiceEncoderKind;
}

const IDLE: RecorderState = { phase: 'idle', owner: null, elapsed: 0, error: null, format: null };

// MARK: - Encoders

interface Sink {
  push(pcm: Float32Array): void;
  /** The finished file's bytes, or null when the encoder failed. */
  finish(): Promise<Uint8Array | null>;
  cancel(): void;
}

class AacSink implements Sink {
  private encoder: AudioEncoder;
  private samples: Mp4Sample[] = [];
  private asc: Uint8Array | null = null;
  private written = 0;
  private failed = false;

  constructor(private sampleRate: number) {
    this.encoder = new AudioEncoder({
      output: (chunk, meta) => {
        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);
        this.samples.push({ data, duration: 1024 });
        const description = meta?.decoderConfig?.description;
        if (description && !this.asc) {
          this.asc = description instanceof ArrayBuffer ? new Uint8Array(description.slice(0)) : new Uint8Array((description as ArrayBufferView).buffer.slice(0));
        }
      },
      error: (e) => {
        this.failed = true;
        console.warn('[satori-voice] AAC encoder failed', e);
      },
    });
    this.encoder.configure({ codec: 'mp4a.40.2', sampleRate, numberOfChannels: 1, bitrate: VOICE_BITRATE });
  }

  push(pcm: Float32Array): void {
    if (this.failed || this.encoder.state !== 'configured') return;
    const data = new AudioData({
      format: 'f32-planar',
      sampleRate: this.sampleRate,
      numberOfFrames: pcm.length,
      numberOfChannels: 1,
      timestamp: Math.round((this.written / this.sampleRate) * 1e6),
      data: pcm as Float32Array<ArrayBuffer>,
    });
    this.written += pcm.length;
    this.encoder.encode(data);
    data.close();
  }

  async finish(): Promise<Uint8Array | null> {
    try {
      if (this.encoder.state === 'configured') await this.encoder.flush();
      this.encoder.close();
    } catch {
      this.failed = true;
    }
    if (this.failed || !this.samples.length) return null;
    const asc = this.asc && this.asc.length >= 2 ? this.asc : audioSpecificConfig(this.sampleRate, 1);
    const entry = mp4aSampleEntry({ sampleRate: this.sampleRate, channels: 1, asc, avgBitrate: VOICE_BITRATE });
    return writeAudioMp4({ timescale: this.sampleRate, sampleEntry: entry, samples: this.samples });
  }

  cancel(): void {
    try {
      this.encoder.close();
    } catch {
      /* already closed */
    }
    this.samples = [];
  }
}

type LameModule = typeof import('@breezystack/lamejs');

class Mp3Sink implements Sink {
  private encoder: InstanceType<LameModule['Mp3Encoder']>;
  private parts: Uint8Array[] = [];
  private pending: Int16Array = new Int16Array(0);

  constructor(lame: LameModule, sampleRate: number) {
    this.encoder = new lame.Mp3Encoder(1, sampleRate, VOICE_BITRATE / 1000);
  }

  push(pcm: Float32Array): void {
    const next = floatTo16(pcm);
    const joined = new Int16Array(this.pending.length + next.length);
    joined.set(this.pending);
    joined.set(next, this.pending.length);
    const whole = joined.length - (joined.length % 1152);
    for (let at = 0; at < whole; at += 1152) {
      const out = this.encoder.encodeBuffer(joined.subarray(at, at + 1152));
      if (out.length) this.parts.push(new Uint8Array(out));
    }
    this.pending = joined.slice(whole);
  }

  async finish(): Promise<Uint8Array | null> {
    if (this.pending.length) {
      const out = this.encoder.encodeBuffer(this.pending);
      if (out.length) this.parts.push(new Uint8Array(out));
    }
    const tail = this.encoder.flush();
    if (tail.length) this.parts.push(new Uint8Array(tail));
    const total = this.parts.reduce((n, p) => n + p.length, 0);
    if (!total) return null;
    const bytes = new Uint8Array(total);
    let at = 0;
    for (const p of this.parts) {
      bytes.set(p, at);
      at += p.length;
    }
    return bytes;
  }

  cancel(): void {
    this.parts = [];
  }
}

/** Safari before WebCodecs audio, and the Opus last resort: the browser's own recorder. */
class MediaRecorderSink implements Sink {
  private recorder: MediaRecorder;
  private chunks: Blob[] = [];

  constructor(stream: MediaStream, private mimeType: string) {
    this.recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: VOICE_BITRATE });
    this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.recorder.start(1000);
  }

  push(): void {}

  pause(): void {
    if (this.recorder.state === 'recording') this.recorder.pause();
  }

  resume(): void {
    if (this.recorder.state === 'paused') this.recorder.resume();
  }

  async finish(): Promise<Uint8Array | null> {
    if (this.recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        this.recorder.onstop = () => resolve();
        this.recorder.stop();
      });
    }
    const bytes = new Uint8Array(await new Blob(this.chunks).arrayBuffer());
    if (!bytes.length) return null;
    if (this.mimeType.startsWith('audio/mp4')) {
      // MediaRecorder writes FRAGMENTED MP4, which AVAudioPlayer does not reliably read; the same
      // samples in a progressive file play everywhere. A file we cannot read is sent as it came.
      const probe = probeMp4(bytes);
      if (probe?.fragmented) return remuxFragmented(bytes) ?? bytes;
    }
    return bytes;
  }

  cancel(): void {
    this.recorder.ondataavailable = null;
    if (this.recorder.state !== 'inactive') this.recorder.stop();
    this.chunks = [];
  }
}

// MARK: - Capabilities

let capabilities: Promise<VoiceEncoderChoice | null> | null = null;

async function aacEncoderAvailable(): Promise<boolean> {
  if (typeof AudioEncoder !== 'function' || typeof AudioData !== 'function') return false;
  try {
    const [a, b] = await Promise.all([
      AudioEncoder.isConfigSupported({ codec: 'mp4a.40.2', sampleRate: 44100, numberOfChannels: 1, bitrate: VOICE_BITRATE }),
      AudioEncoder.isConfigSupported({ codec: 'mp4a.40.2', sampleRate: 48000, numberOfChannels: 1, bitrate: VOICE_BITRATE }),
    ]);
    return !!a.supported && !!b.supported;
  } catch {
    return false;
  }
}

function mediaRecorderSupports(type: string): boolean {
  try {
    return typeof MediaRecorder === 'function' && MediaRecorder.isTypeSupported(type);
  } catch {
    return false;
  }
}

/** Which encoder this browser gets (asked once). `?voiceFormat=mp3|aac…` forces one for testing. */
export function encoderChoice(): Promise<VoiceEncoderChoice | null> {
  capabilities ??= (async () => {
    const forced = new URLSearchParams(location.search).get('voiceFormat');
    const caps = {
      aacEncoder: forced ? forced === 'aac-webcodecs' : await aacEncoderAvailable(),
      mediaRecorderAac: forced ? forced === 'aac-mediarecorder' : mediaRecorderSupports('audio/mp4;codecs=mp4a.40.2'),
      audioWorklet: typeof AudioWorkletNode === 'function' && (forced ? forced !== 'opus-mediarecorder' && forced !== 'aac-mediarecorder' : true),
      mediaRecorderOpus: mediaRecorderSupports('audio/webm;codecs=opus'),
    };
    return chooseVoiceEncoder(caps);
  })();
  return capabilities;
}

// MARK: - The recorder

const coarse = () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/** Everything one take holds open; torn down as a unit so a late teardown can never touch the next take. */
interface Session {
  owner: string;
  choice: VoiceEncoderChoice;
  stream: MediaStream | null;
  context: AudioContext | null;
  source: MediaStreamAudioSourceNode | null;
  analyser: AnalyserNode | null;
  capture: AudioWorkletNode | null;
  sink: Sink | null;
  startedAt: number;
  accumulatedPaused: number;
  pausedAt: number | null;
}

const MIC_CONSTRAINTS: MediaStreamConstraints = { audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } };

class VoiceRecorderController {
  private state: RecorderState = IDLE;
  private listeners = new Set<() => void>();
  private session: Session | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  private meterBuffer: Float32Array<ArrayBuffer> | null = null;
  private wired = false;

  get snapshot(): RecorderState {
    return this.state;
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => void this.listeners.delete(l);
  };

  private set(patch: Partial<RecorderState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private now(): number {
    return performance.now() / 1000;
  }

  /** Read fresh: the phase changes across every await. */
  private phase(): RecorderPhase {
    return this.state.phase;
  }

  private recordedSoFar(s: Session | null = this.session): number {
    if (!s || !s.startedAt) return 0;
    return VoiceRecordingClock.recorded(s.startedAt, s.accumulatedPaused, s.pausedAt, this.now());
  }

  /** The live level, 0…1 (the strip's meter reads it each frame). */
  level(): number {
    const analyser = this.session?.analyser;
    if (!analyser || this.state.phase !== 'recording') return 0;
    const buffer = (this.meterBuffer ??= new Float32Array(new ArrayBuffer(analyser.fftSize * 4)));
    analyser.getFloatTimeDomainData(buffer);
    return meterLevel(buffer);
  }

  dismissError(owner: string): void {
    if (this.state.error?.owner === owner) this.set({ error: null });
  }

  /** Asks for the microphone on first use; false = could not start (the error says why). */
  async start(owner: string): Promise<boolean> {
    if (this.state.phase !== 'idle') return false;
    this.wire();
    this.set({ phase: 'starting', owner, error: null, elapsed: 0 });
    const fail = (kind: MicError, session?: Session) => {
      if (session) this.tearDown(session);
      if (this.state.owner === owner) this.set({ ...IDLE, error: { owner, kind } });
      return false;
    };
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return fail('unsupported');
    const choice = await encoderChoice();
    if (!choice) return fail('unsupported');
    // The MP3 encoder is fetched BEFORE the microphone opens, so the first word is not lost to a
    // download (and a failed download is a clean refusal, not a half-started take).
    let lame: LameModule | null = null;
    if (choice.kind === 'mp3') {
      try {
        lame = await import('@breezystack/lamejs');
      } catch {
        return fail('failed');
      }
    }
    const session: Session = { owner, choice, stream: null, context: null, source: null, analyser: null, capture: null, sink: null, startedAt: 0, accumulatedPaused: 0, pausedAt: null };
    try {
      session.stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    } catch (e) {
      return fail(micErrorKind(e));
    }
    if (this.state.owner !== owner || this.phase() !== 'starting') {
      // Cancelled (or the conversation changed) while the permission prompt was up.
      this.tearDown(session);
      return false;
    }
    try {
      await this.openGraph(session);
      const rate = session.context!.sampleRate;
      if (choice.kind === 'aac-webcodecs') session.sink = new AacSink(rate);
      else if (choice.kind === 'mp3') session.sink = new Mp3Sink(lame!, rate);
      else session.sink = new MediaRecorderSink(session.stream, choice.kind === 'aac-mediarecorder' ? 'audio/mp4;codecs=mp4a.40.2' : 'audio/webm;codecs=opus');
    } catch (e) {
      console.warn('[satori-voice] could not start the recorder', e);
      return fail('failed', session);
    }
    if (this.state.owner !== owner || this.phase() !== 'starting') {
      this.tearDown(session);
      return false;
    }
    this.watchTracks(session.stream);
    session.startedAt = this.now();
    this.session = session;
    this.set({ phase: 'recording', elapsed: 0, format: choice.kind });
    this.ticker = setInterval(() => this.set({ elapsed: this.recordedSoFar() }), 200);
    void this.holdScreen(true);
    console.info('[satori-voice] recording', choice.kind, `${session.context?.sampleRate ?? 0} Hz`);
    return true;
  }

  /** WebAudio graph: source → analyser (meter) and → capture worklet (PCM paths). */
  private async openGraph(s: Session): Promise<void> {
    let context: AudioContext;
    let source: MediaStreamAudioSourceNode;
    try {
      // The apps' 44.1 kHz; Firefox refuses to connect a microphone to a context at another rate
      // than the device's, and then the device's own rate (48 kHz) is kept — AAC/MP3 play it alike.
      context = new AudioContext({ sampleRate: VOICE_SAMPLE_RATE, latencyHint: 'interactive' });
      try {
        source = context.createMediaStreamSource(s.stream!);
      } catch (e) {
        void context.close();
        throw e;
      }
    } catch {
      context = new AudioContext({ latencyHint: 'interactive' });
      source = context.createMediaStreamSource(s.stream!);
    }
    s.context = context;
    s.source = source;
    s.analyser = context.createAnalyser();
    s.analyser.fftSize = 1024;
    source.connect(s.analyser);
    if (s.choice.kind === 'aac-webcodecs' || s.choice.kind === 'mp3') {
      await context.audioWorklet.addModule(workletUrl);
      const capture = new AudioWorkletNode(context, 'satori-voice-capture', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
      capture.port.onmessage = (e: MessageEvent<Float32Array>) => {
        if (this.session === s && this.state.phase === 'recording') s.sink?.push(e.data);
      };
      // A silent tail to the destination keeps the node pulled in every browser.
      const mute = context.createGain();
      mute.gain.value = 0;
      source.connect(capture);
      capture.connect(mute).connect(context.destination);
      s.capture = capture;
    }
    if (context.state === 'suspended') await context.resume();
  }

  private watchTracks(stream: MediaStream | null): void {
    for (const track of stream?.getAudioTracks() ?? []) {
      track.addEventListener('ended', this.onTrackLost);
      track.addEventListener('mute', this.onTrackLost);
    }
  }

  private onTrackLost = () => this.pause();

  /** Pauses the take, keeping the file and the microphone. */
  pause(): void {
    const s = this.session;
    if (!s || this.state.phase !== 'recording') return;
    s.capture?.port.postMessage('flush');
    if (s.sink instanceof MediaRecorderSink) s.sink.pause();
    s.pausedAt = this.now();
    this.set({ phase: 'paused', elapsed: this.recordedSoFar() });
  }

  /** Picks the same take back up; a microphone that died meanwhile is opened again. */
  async resume(): Promise<void> {
    const s = this.session;
    if (!s || this.state.phase !== 'paused') return;
    const live = s.stream?.getAudioTracks().some((t) => t.readyState === 'live' && !t.muted);
    if (!live && !(s.sink instanceof MediaRecorderSink)) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
        if (this.session !== s) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        this.releaseStream(s);
        s.stream = stream;
        s.source?.disconnect();
        s.source = s.context!.createMediaStreamSource(stream);
        s.source.connect(s.analyser!);
        if (s.capture) s.source.connect(s.capture);
        this.watchTracks(stream);
      } catch (e) {
        this.set({ error: { owner: s.owner, kind: micErrorKind(e) } });
        return;
      }
    }
    if (s.context?.state === 'suspended') await s.context.resume();
    if (s.sink instanceof MediaRecorderSink) s.sink.resume();
    if (s.pausedAt != null) s.accumulatedPaused = VoiceRecordingClock.accumulatedAfterResume(s.accumulatedPaused, s.pausedAt, this.now());
    s.pausedAt = null;
    this.set({ phase: 'recording', error: null });
  }

  togglePause(): void {
    if (this.state.phase === 'paused') void this.resume();
    else this.pause();
  }

  /** Stops and returns the finished take, or null when it was too short (or the encoder failed). */
  async finish(): Promise<VoiceTake | null> {
    const s = this.session;
    if (!s || (this.state.phase !== 'recording' && this.state.phase !== 'paused')) return null;
    // The RECORDED length, not the wall clock (VoiceRecorder.finish): a pause must not let an
    // empty note past the minimum.
    const duration = this.recordedSoFar(s);
    // The composer is back the moment send is pressed; the take finishes encoding behind it.
    this.detach();
    this.set({ ...IDLE });
    const sink = s.sink;
    s.sink = null;
    if (!sink || duration < VOICE_MIN_DURATION) {
      sink?.cancel();
      this.tearDown(s);
      return null;
    }
    // The last batch still in the worklet reaches the encoder before the graph closes.
    if (s.capture) {
      s.capture.port.onmessage = (e: MessageEvent<Float32Array>) => sink.push(e.data);
      s.capture.port.postMessage('flush');
      await new Promise((r) => setTimeout(r, 80));
    }
    let bytes: Uint8Array | null = null;
    try {
      bytes = await sink.finish();
    } catch (e) {
      console.warn('[satori-voice] encoding failed', e);
    }
    this.tearDown(s);
    if (!bytes?.length) {
      if (this.phase() === 'idle') this.set({ error: { owner: s.owner, kind: 'failed' } });
      return null;
    }
    const name = voiceNoteName(new Date(), s.choice.extension);
    return { file: new File([bytes as Uint8Array<ArrayBuffer>], name, { type: s.choice.mimeType }), duration, format: s.choice.kind };
  }

  /** Throws the take away. */
  cancel(): void {
    if (this.state.phase === 'idle') return;
    const s = this.session;
    this.detach();
    this.set({ ...IDLE });
    if (s) {
      s.sink?.cancel();
      s.sink = null;
      this.tearDown(s);
    }
  }

  /** The page stops showing this take (ticker, wake lock, meter); its resources go in tearDown. */
  private detach(): void {
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
    this.session = null;
    this.meterBuffer = null;
    void this.holdScreen(false);
  }

  private releaseStream(s: Session): void {
    for (const track of s.stream?.getTracks() ?? []) {
      track.removeEventListener('ended', this.onTrackLost);
      track.removeEventListener('mute', this.onTrackLost);
      track.stop();
    }
    s.stream = null;
  }

  private tearDown(s: Session): void {
    if (s.capture) s.capture.port.onmessage = null;
    s.capture?.disconnect();
    s.capture = null;
    s.source?.disconnect();
    s.source = null;
    s.analyser = null;
    void s.context?.close().catch(() => undefined);
    s.context = null;
    this.releaseStream(s);
  }

  // The take's own KEY on the page's one wake-lock writer (ui/screenAwake.ts, PARITY P304): phones
  // only (a desktop screen does not sleep under a short take), and releasing it never drops a call's.
  private readonly screenKey = ScreenAwake.nextVoiceNoteKey();

  private async holdScreen(held: boolean): Promise<void> {
    screenAwake.set(this.screenKey, held && coarse() && this.state.phase !== 'idle');
  }

  private wire(): void {
    if (this.wired || typeof window === 'undefined') return;
    this.wired = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && coarse()) this.pause();
    });
    window.addEventListener('satori:media-interrupt', () => this.pause());
    window.addEventListener('pagehide', () => this.cancel());
  }
}

export const voiceRecorder = new VoiceRecorderController();

/** For the calls feature: a call starting in this tab pauses a take (never resumes it). */
export function pauseVoiceRecording(): void {
  voiceRecorder.pause();
}

export function useVoiceRecorder(): RecorderState {
  return useSyncExternalStore(voiceRecorder.subscribe, () => voiceRecorder.snapshot);
}
